import type { fabric } from 'fabric';
import { createObjectUid } from '@/lib/objectUid';

/** Persist lock/frame/guide flags (also accept legacy pixkit* keys when loading). */
export const LAYER_CUSTOM_PROPS = [
  'version',
  'craftaraLocked',
  'pixkitLocked',
  'name',
  '__uid',
  'elementId',
  'craftaraFrame',
  'pixkitFrame',
  'craftaraGuide',
  'pixkitGuide',
  // motion editor
  'craftaraAnim', // LayerAnimation
  'craftaraVideo', // VideoClipRef on fabric.CraftaraVideo
  'craftaraVideo', // VideoClipRef (on fabric.CraftaraVideo)
] as const;

type Lockable = fabric.Object & {
  craftaraLocked?: boolean;
  pixkitLocked?: boolean;
};

export function isObjectLocked(obj: fabric.Object | null | undefined): boolean {
  if (!obj) return false;
  const o = obj as Lockable;
  return Boolean(o.craftaraLocked || o.pixkitLocked);
}

export function setObjectLocked(obj: fabric.Object, locked: boolean): void {
  const o = obj as Lockable;
  o.craftaraLocked = locked;
  if ('pixkitLocked' in o) delete o.pixkitLocked;
  obj.set({
    lockMovementX: locked,
    lockMovementY: locked,
    lockScalingX: locked,
    lockScalingY: locked,
    lockRotation: locked,
    hasControls: !locked,
    // Keep selectable false when locked so drag-transform is blocked;
    // Layers panel still uses setActiveObject.
    selectable: !locked,
    evented: !locked,
  });
  obj.setCoords();
}

export function renameObject(obj: fabric.Object, name: string): void {
  (obj as fabric.Object & { name?: string }).name = name.trim() || undefined;
}

export function duplicateObject(
  canvas: fabric.Canvas,
  obj: fabric.Object
): Promise<fabric.Object | null> {
  return new Promise((resolve) => {
    // Without propertiesToInclude, clone() drops name / lock / animation props.
    obj.clone((cloned: fabric.Object) => {
      if (!cloned) {
        resolve(null);
        return;
      }
      cloned.set({
        left: (obj.left || 0) + 24,
        top: (obj.top || 0) + 24,
      });
      const c = cloned as fabric.Object & { __uid?: string } & Lockable;
      c.__uid = createObjectUid();
      if (isObjectLocked(cloned)) setObjectLocked(cloned, false);
      canvas.add(cloned);
      canvas.setActiveObject(cloned);
      canvas.requestRenderAll();
      resolve(cloned);
    }, [...LAYER_CUSTOM_PROPS]);
  });
}

/** Remove a layer from the canvas. Locked objects are skipped (same as Delete key). */
export function deleteObject(canvas: fabric.Canvas, obj: fabric.Object): boolean {
  if (isObjectLocked(obj)) return false;
  if (canvas.getActiveObject() === obj) {
    canvas.discardActiveObject();
  }
  canvas.remove(obj);
  canvas.requestRenderAll();
  return true;
}
